import { useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Typography, Stack, CircularProgress } from "@mui/material"
import { Player } from "@lottiefiles/react-lottie-player"
import { useSearchParams } from "react-router-dom"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { usePublicApiMutation } from "../../../../core/api/useApiMutation"
import loadingAnimationSuccess from "../../../login/assets/success-lottie-anim.json"
import loadingAnimationFailure from "../../../login/assets/failure-lottie-anim.json"
import { confirmBooking } from "../confirmBookingApi"
import { AppError } from "../../../../core/api/errorTypes"

const useStyles = makeStyles()(() => ({
    animationSuccess: {
        height: "8rem",
    },
    animationFailed: {
        height: "6rem",
    },
}))

export function useConfirmBookingParams() {
    const [searchParams] = useSearchParams()
    const orderUid = searchParams.get("orderUid")

    if (!orderUid) {
        throw new AppError("E_INVALID_LINK", "Invalid link", "Link was missing information: orderUid")
    }

    return { orderUid }
}
export function ConfirmBooking() {
    const { classes } = useStyles()
    const { orderUid } = useConfirmBookingParams()

    const [message, setMessage] = useState<{ type: "success" | "error" | null; text: string | null }>({
        type: null,
        text: null,
    })

    const { mutate: callAConfirmBooking, isLoading } = usePublicApiMutation(confirmBooking, {
        async onSuccess(_, data) {
            if (data?.success) {
                setMessage({
                    type: "success",
                    text: data?.serviceResponse?.displayMessage || "You have successfully confirmed your booking",
                })
            } else {
                setMessage({
                    type: "error",
                    text: data?.serviceResponse?.displayMessage || "Booking confirmation failed.",
                })
            }
        },
        async onError(_, error) {
            const errorMsg =
                error && typeof error === "object" && "displayMessage" in error
                    ? (error?.displayMessage as string)
                    : "Something went wrong. Try again."
            setMessage({ type: "error", text: errorMsg })
        },
    })

    useEffect(() => {
        callAConfirmBooking({ orderUid })
    }, [orderUid, callAConfirmBooking])

    const renderMessageContent = () => {
        if (isLoading) {
            return (
                <>
                    <CircularProgress size={85} />
                    <LocalizedStrict id="confirm-booking-loading">
                        <Typography
                            variant="h4"
                            fontWeight={600}
                            marginTop="4rem"
                            marginBottom="2rem"
                            textAlign="center"
                        >
                            Loading...
                        </Typography>
                    </LocalizedStrict>
                </>
            )
        }

        const isSuccess = message.type === "success"
        const animationSrc = isSuccess ? loadingAnimationSuccess : loadingAnimationFailure
        const animationClass = isSuccess ? classes.animationSuccess : classes.animationFailed

        return (
            <>
                <Player keepLastFrame src={animationSrc} autoplay speed={1} className={animationClass} />
                <LocalizedStrict id={isSuccess ? "confirm-booking-complete" : "confirm-booking-failed"}>
                    <Typography variant="h4" fontWeight={600} marginY="2rem" textAlign="center">
                        {isSuccess ? "Congratulation" : "Failed"}
                    </Typography>
                </LocalizedStrict>
                <Typography maxWidth="90%" marginBottom="5rem" variant="h6" textAlign="center">
                    {message.text}
                </Typography>
            </>
        )
    }

    return <Stack alignItems="center">{renderMessageContent()}</Stack>
}
